import React, { Dispatch, SetStateAction } from 'react'
import { Form, Formik } from 'formik'

import { Button, Modal, useAuthState, useShowMessage } from '@pepper/ui'
import { editCompanyDetails } from '@pepper/utils/src/api/users/users'

import { getFieldComponent } from 'components/FormFields/FormFields'
import {
  CompanyInfo,
  JOURNEYSTAGE,
} from 'container/CreateOrganisationJourney/CreateOrganisationJourney'
import OrganisationCountry from 'container/OnboardingUser/OrganisationCountry/OrganisationCountry'
import { createOrganisation } from 'utils/api/createOrganisation'

import { checkForHttp, fields, validationSchema } from './schema'
import styles from './_.module.css'

interface Values {
  organisationName: string
  organisationWebsite: string
  country_code: string
}

interface Props {
  modal: boolean
  setModal: Dispatch<SetStateAction<boolean>>
  companyId: string
  company: { organisationName: string; organisationWebsite: string }
  setCompany: Dispatch<SetStateAction<CompanyInfo>>
  setJourneyState: Dispatch<SetStateAction<JOURNEYSTAGE>>
  setCompanyID: Dispatch<SetStateAction<string>>
}

const CreateOrganisationModal = ({
  modal,
  company,
  companyId,
  setModal,
  setCompany,
  setJourneyState,
  setCompanyID,
}: Props): JSX.Element => {
  const { getAuthUser } = useAuthState()
  const message = useShowMessage()

  const handleSubmit = async (values: Values) => {
    try {
      const value = {
        company_name: values.organisationName,
        company_website: checkForHttp(values.organisationWebsite),
        country_code: values.country_code,
      }

      const response = companyId
        ? await editCompanyDetails({ id: companyId, body: value })
        : await createOrganisation(value)

      if (response.status === 'error') {
        message({ type: 'error', message: 'Something unexpected happened' })
        return
      }

      await getAuthUser()

      // forcefully redirect user to / route & clear cache
      // window.location.href = `/${response.data.id}`
      setCompany(values)
      if ('data' in response) {
        setCompanyID(response.data.id)
      }
      message({
        type: 'success',
        message: `${response.company_name} has been created successfully`,
      })
      setModal(false)
      // setJourneyState(JOURNEYSTAGE.FETCHING_ORG_DETAILS)
    } catch (error) {
      message({ type: 'error', message: error.message })
    }
  }

  const handleOnCancelModal = () => {
    setJourneyState(JOURNEYSTAGE.CLOSED)
    setModal(false)
  }

  return (
    <Formik
      initialValues={{ ...company, country_code: 'IN' }}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {formik => (
        <Form id="create-new-organisation-form">
          <Modal
            visible={modal}
            centered={true}
            title="Help us understand you better"
            okText="Create"
            className={styles.modal}
            maskClosable={false}
            closable={true}
            onCancel={handleOnCancelModal}
            okButtonProps={{
              htmlType: 'submit',
              form: 'create-new-organisation-form',
              loading: formik.isSubmitting,
            }}
            footer={[
              <Button
                onClick={formik.submitForm}
                key="submit"
                type="primary"
                disabled={formik.isSubmitting}
                form="create-new-organisation-form"
                htmlType="submit"
                className={styles.input_button_icon}
              >
                Create
              </Button>,
            ]}
          >
            {fields.map(field => (
              <div key={field.name} className={styles.field}>
                {getFieldComponent({ ...field, actions: formik })}
              </div>
            ))}
            <div className={styles.field}>
              <label htmlFor="country_code">Organisation country:</label>
              <OrganisationCountry name="country_code" />
            </div>
          </Modal>
        </Form>
      )}
    </Formik>
  )
}

export default CreateOrganisationModal
